import { redirect } from 'next/navigation';
import { z, ZodError } from 'zod';

import {
  ALL_AVAILABLE_SPECIALS,
  ALL_SPECIALS_FIELDS,
  FILTER_FOR_ALL_CAR_SPECIALS,
  FILTER_FOR_ALL_NEW_CAR_SPECIALS,
  FILTER_FOR_ALL_USED_CAR_SPECIALS,
  KIA_BRAND_ID,
  KIA_BRAND_MODELS_FIELDS,
  SEO_SPECIAL,
  SPECIALS_CATEGORIES_FIELDS,
  SPECIALS_CATEGORIES_PATHS_FIELDS,
  SPECIALS_ITEMS_COUNT,
  SPECIALS_ITEMS_COUNT_META_DATA,
  VEHICLE_TYPE_FILTER,
} from '@/constants/specials/specials.constants';
import {
  FILTER_ACTIVE_VEHICLES,
  VEHICLES_REQUEST_FIELDS,
} from '@/constants/used-cars/used-cars.constants';
import {
  FOR_SALE,
  KIA_GROUP,
  PATH_COUNT,
  SOUTH_AFRICA,
} from '@/constants/vars.constants';
import { directusService, limiter, zodKeys } from '@/services/api.service';
import { getAllBrands } from '@/services/CarService.service';
import { mapVehicleResponse } from '@/services/details-car/details-car.service';
import {
  mapKiaModelsResponse,
  mapRangesResponse,
  mapSpecialsCategoriesPaths,
  mapSpecialsResponse,
  variableForTitleSpecials,
} from '@/services/specials/specials.service';
import {
  KiaModelsReq,
  KiaRangesReq,
  RangeReq,
  SpecialsReq,
} from '@/types/request/request.types';
import { SpecialCategory } from '@/types/specials/specials.types';
import { SpecialFor, VehicleType } from '@/types/used-cars/used-cars.types';

import { getBrandNames } from './CarService.service';
import { getImageLink } from './link.service';

export const getKiaRanges = limiter.wrap(async ({ params = {} }) => {
  const { data } = await directusService.get<KiaRangesReq[]>(`items/ranges`, {
    params: {
      filter: {
        brand_id: { _eq: KIA_BRAND_ID },
        ...(params?.filter ?? {}),
      },
    },
  });

  return mapRangesResponse(data);
});

export const getRanges = limiter.wrap(async ({ params = {} }) => {
  const { data } = await directusService.get<RangeReq[]>(`items/ranges`, {
    params,
  });

  return mapRangesResponse(data);
});

export const getKiaModels = limiter.wrap(async () => {
  const filterSpecialByStatus =
    'deep[range_ids][specials][_filter][status][_eq]=approved';
  const filterSpecialByStartDate =
    'deep[range_ids][specials][_filter][start_date][_lte]=$NOW';
  const filterSpecialByEndDate =
    'deep[range_ids][specials][_filter][end_date][_gte]=$NOW';
  const { data } = await directusService.get<KiaModelsReq[]>(
    `items/brands/${KIA_BRAND_ID}?${filterSpecialByStatus}&${filterSpecialByStartDate}&${filterSpecialByEndDate}`,
    {
      params: { fields: KIA_BRAND_MODELS_FIELDS },
    },
  );

  return mapKiaModelsResponse(data);
});

export const getVariants = async (rangeId: number) => {
  const { data: vehicleModels } = (await directusService.get('items/vehicles', {
    params: {
      filter: {
        ...FILTER_ACTIVE_VEHICLES,
        range_id: {
          _eq: rangeId,
        },
      },
      groupBy: 'model',
      fields: ['id', 'model.id', 'model.name'].join(','),
    },
  })) as { data: { model: number }[] };

  const { data } = (await directusService.get<{ id: number; name: string }[]>(
    'items/model',
    {
      params: {
        filter: {
          id: {
            _in: vehicleModels.map(({ model }) => model),
          },
        },
        fields: ['id', 'name'],
      },
    },
  )) as { data?: { id: number; name: string }[] };

  return data || [];
};

export const getPriceCrushSpecials = limiter.wrap(async (filter = {}) => {
  const { data } = await directusService.get<SpecialsReq[]>(`items/specials/`, {
    params: {
      filter,
      fields: ALL_SPECIALS_FIELDS,
    },
  });

  return mapSpecialsResponse(data);
});

export const newKiaSpecialsCarsResponse = (cars: any[]) => {
  const getNewKiaSpecials: any[] = [];

  cars.map((car) => {
    if (car.specials.length != 0) {
      getNewKiaSpecials.push({
        title:
          car.name.charAt(0).toUpperCase() + car.name.slice(1).toLowerCase(),
        link: `/specials/brand/kia/${car.name.toLowerCase()}`,
      });
    }
  });
  return getNewKiaSpecials;
};

const SpecialContentBlockSchema = z.object({
  title: z.string(),
  description: z.string(),
  image: z.string(),
});

const SpecialDetailsSchema = z.object({
  id: z.number(),
  title: z.string(),
  sub_title: z.string(),
  cashback: z.boolean(),
  cashback_amount: z.number().nullish(),
  image: z.string().nullish(),
  banner_image: z.string().nullish(),
  monthly_repayment: z.coerce.number().nullish(),
  payment_terms: z.coerce.number().nullish(),
  deposit: z.coerce.number(),
  residual: z.coerce.number().nullish(),
  interest_rate: z.coerce.number(),
  vehicle_type: z.nativeEnum(VehicleType),
  source: z.string().nullish(),
  mileage: z.number().nullish(),
  special_for: z.nativeEnum(SpecialFor),
  year: z.number().nullish(),
  vehicles: z
    .object({
      id: z.number(),
      spec_body_colors: z
        .object({
          name: z.string(),
        })
        .nullish(),
      vin: z.string(),
      registration_number: z.string().nullish(),
      spec_drivetrain: z.object({ value: z.string() }),
      spec_transmission: z.object({ name: z.string() }),
      spec_fuel_types: z.object({ name: z.string() }),
      spec_doors: z.object({ name: z.string() }),
      spec_body_type: z.object({ name: z.string() }),
      main_image: z.string(),
      images: z.array(z.string()),
    })
    .nullish(),
  vehicle_price: z.number().nullish(),
  range: z
    .object({
      id: z.number(),
      name: z.string(),
    })
    .nullish(),
  brand: z
    .object({
      id: z.number(),
      name: z.string(),
    })
    .nullish(),
  mmcode: z.string().nullish(),
  description: z.string().nullish(),
  special_category: z
    .object({
      id: z.number(),
    })
    .nullish(),
  sale_price_incl_vat: z
    .string()
    .nullish()
    .transform((str) => (str ? str.replace(/[^\d.-]/g, '') : ''))
    .pipe(z.coerce.number()),
  short_description: z.string().nullish(),
  dealerships: z.array(
    z.object({
      dealerships_id: z.object({
        id: z.string(),
        name: z.string(),
        cms_used_dealer_code: z.string(),
        cms_new_dealer_code: z.string(),
        cms_used_leads: z.string(),
        cms_new_leads: z.string(),
        city: z
          .object({
            id: z.number(),
            name: z.string(),
            province: z.object({
              id: z.number(),
              name: z.string(),
            }),
            country: z
              .object({
                name: z.string(),
              })
              .nullish(),
          })
          .nullish(),
      }),
    }),
  ),
  model: z
    .object({
      id: z.number(),
      status: z.string(),
      sort: z.number().nullish(),
      name: z.string(),
      image: z.string().nullish(),
      intro_date: z.number().nullish(),
      year: z.number(),
      fuel_type: z.string().nullish(),
      disc_date: z.union([z.string(), z.number()]).nullish(),
      price: z.string().nullish(),
      range: z.number().nullish(),
      brand_code: z.string().nullish(),
      range_code: z.string().nullish(),
      mmcode: z.string().nullish(),
    })
    .nullish(),
  content_blocks: z
    .array(z.union([z.string(), SpecialContentBlockSchema]))
    .nullish(),
  call_to_actions: z
    .array(
      z
        .object({
          call_to_actions_id: z.object({
            id: z.number(),
            text: z.string().optional().nullable(),
            heading: z.string().nullable().optional(),
            subheading: z.string().nullable().optional(),
            orientation: z.string().optional().nullable(),
            url: z.string().optional().nullish(),
            icon: z.string().optional().nullish(),
            image: z.string().nullable().optional(),
          }),
        })
        .optional(),
    )
    .nullish(),
});

export type SpecialContentBlock = z.infer<typeof SpecialContentBlockSchema>;

export type SpecialDetails = z.infer<typeof SpecialDetailsSchema>;

export const getSpecialDetails = async (specialId: string) => {
  try {
    const { data } = await directusService.get(`items/specials/${specialId}`, {
      params: {
        fields: zodKeys(SpecialDetailsSchema),
        limit: -1,
      },
    });

    return SpecialDetailsSchema.parse(data);
  } catch (error) {
    console.error(
      'Special Details data :',
      error instanceof ZodError ? error.errors : null,
    );
    return;
  }
};

export const getAllAvailableSpecials = async <T = SpecialsReq[]>(
  filter = {},
  params = {},
) => {
  const { data } = await directusService.get<T>(`items/specials/`, {
    params: {
      filter,
      fields: ALL_AVAILABLE_SPECIALS,
      limit: -1,
      sort: 'sort',
      ...params,
    },
  });

  return data as T;
};

export const getSpecialsCategoriesPaths = limiter.wrap(async () => {
  const { data } = await directusService.get<SpecialCategory[]>(
    'items/special_categories',
    {
      params: { fields: SPECIALS_CATEGORIES_PATHS_FIELDS },
    },
  );

  return mapSpecialsCategoriesPaths(data.slice(0, PATH_COUNT));
});

export const getSpecialsCategories = limiter.wrap(async ({ params }) => {
  const { data } = await directusService.get<SpecialCategory[]>(
    'items/special_categories',
    {
      params: { fields: SPECIALS_CATEGORIES_FIELDS, ...params },
    },
  );

  return data?.length > 0 ? data : null;
});

export const getCategoryByValue = limiter.wrap(
  async (value: string): Promise<SpecialCategory | null> => {
    const { data } = await directusService.get<SpecialCategory[]>(
      'items/special_categories',
      {
        params: {
          fields: SPECIALS_CATEGORIES_FIELDS,
          filter: { value: { _eq: value } },
        },
      },
    );

    return data?.length > 0 ? data[0] : null;
  },
);

export const getSpecialsByCategory = limiter.wrap(async (metaQuery, id) => {
  if (!id) {
    return {
      data: [],
      meta: { filter_count: 0 },
    };
  }

  const offset = (metaQuery.currentPage - 1) * SPECIALS_ITEMS_COUNT || 0;

  const { data, meta }: any = await directusService.get('items/specials', {
    params: {
      limit: SPECIALS_ITEMS_COUNT,
      filter: {
        special_category: { _eq: id },
        start_date: { _lte: '$NOW' },
        end_date: { _gte: '$NOW' },
        status: { _eq: 'approved' },
      },
      meta: 'filter_count',
      offset,
    },
  });

  const respData = mapSpecialsResponse(data);

  return {
    data: respData,
    meta,
  };
});

export const getDataByBrandAndRange = async (
  brand: string,
  range: string,
  query: string,
) => {
  const filter: any = {};
  const kiaModels = await getKiaModels();
  const brands = await getAllBrands();
  const currentBrand = brands?.find(
    (currentBrand) =>
      currentBrand.name.toLowerCase() == (brand as string).toLowerCase(),
  );
  const currentBrandId = currentBrand?.id?.toString();

  if (brand) {
    filter['brand'] = {
      _eq: currentBrandId,
    };
  }

  if (range) {
    const value = range?.toString();
    const model = kiaModels.filter(
      (model) => model?.name?.toLowerCase() == value.toLowerCase(),
    );

    if (model && model.length) {
      filter['range'] = {
        _eq: model[0]?.id?.toString(),
      };
    }
  }

  filter.start_date = { _lte: '$NOW' };
  filter.end_date = { _gte: '$NOW' };
  filter.status = { _eq: 'approved' };

  const title = variableForTitleSpecials(filter, query);

  const otherSpecials = await getOtherSpecials();
  const { responseData: category, meta } = await getSpecialByBrand(
    SPECIALS_ITEMS_COUNT_META_DATA,
    filter,
    title,
    SPECIALS_ITEMS_COUNT,
    true,
  );

  return {
    otherSpecials,
    category,
    meta,
    filter,
  };
};

export const generateRangeFilter = async (query: any) => {
  const filter: any = {};
  const kiaRanges = await getKiaRanges({});
  const { brand, range } = query;

  if (brand) {
    filter['brand'] = {
      _eq: KIA_BRAND_ID.toString(),
    };
  }

  let kiaSpecialIds: number[] = [];

  if (range) {
    const value = range?.toString();

    const kiaRange = kiaRanges.filter((range) => {
      if (
        range?.name?.toLowerCase().indexOf(value.toLowerCase()) === 0 &&
        range.name.toLowerCase() !== value.toLowerCase()
      ) {
        return true;
      }

      return (
        range?.name?.replace(/ /g, '-').toLowerCase() === value.toLowerCase()
      );
    });

    kiaRange.forEach((range) => {
      kiaSpecialIds = [...kiaSpecialIds.concat(...(range?.specials || []))];
    });

    if (kiaSpecialIds && kiaSpecialIds.length > 0) {
      filter['id'] = {
        _in: kiaSpecialIds,
      };
    }
  }

  filter.start_date = { _lte: '$NOW' };
  filter.end_date = { _gte: '$NOW' };
  filter.status = { _eq: 'approved' };

  return filter;
};

const getSpecialTitle = (
  variableForTitleSpecials: string,
  data: any,
): string => {
  switch (variableForTitleSpecials) {
    case 'all':
      return 'List of specials';
    case 'used_cars_offer':
      return `Used Cars with Special Offers`;
    case 'demo_cars_offer':
      return `Demo Cars with Special Offers`;
    case 'discount':
      return `Price Crush Specials`;
    case 'offers':
      return `Spectacular Savings Offers`;
    case 'range':
      return `${data?.length !== 0 ? data[0]?.brand?.name : ''} ${
        data.length !== 0 ? data[0]?.range?.name : ''
      } Special`;
    default:
      return `${data?.length !== 0 ? data[0]?.brand?.name : ''} Special`;
  }
};

export const getSpecialByBrand = limiter.wrap(
  async (metaQuery, filter, variableForTitleSpecials, limit, showList) => {
    const offset = (metaQuery.currentPage - 1) * SPECIALS_ITEMS_COUNT || 0;
    const { data, meta }: any = await directusService.get<SpecialsReq[]>(
      'items/specials',
      {
        params: {
          filter,
          fields: ['*', 'brand.*', 'range.*', 'special_category.*', 'source'],
          limit,
          offset,
          meta: 'filter_count',
        },
      },
    );
    const title =
      data?.length !== 0 || showList
        ? getSpecialTitle(variableForTitleSpecials, data)
        : '';
    const responseData = {
      id: 0,
      title: title,
      value: '',
      description: '',
      specials: mapSpecialsResponse(data),
    };

    return { responseData, meta };
  },
);

export const getSpecialsIndexProps = limiter.wrap(async (query) => {
  const type = query.carType ? query.carType.toString() : null;

  if (type !== 'used-cars' && type !== 'new-cars' && type !== 'all-cars') {
    if (type === 'new-kia-specials') {
      redirect('/specials/new-cars');
    } else {
      redirect('/specials');
    }
  }

  const otherSpecials = await getOtherSpecials();

  const filter: any = {};

  if (query.type && query.type !== 'all-cars') {
    if ((VEHICLE_TYPE_FILTER as any)[query.type.toString()]) {
      filter.vehicle_type = {
        _in: (VEHICLE_TYPE_FILTER as any)[query.type.toString()],
      };
    }
  }

  let brand = null;

  if (query.brand && typeof query.brand === 'string') {
    const brands = await getBrandNames();

    brand = brands.find(
      (brand) =>
        brand.name.toLowerCase() ===
        query.brand.toString().replace(/-/gi, ' ').toLowerCase(),
    );
    if (!brand) {
      redirect('/specials');
    }
    filter.brand = {
      _eq: brand.id,
    };
  }

  let rangeList: any[] = [];

  if (query.model && typeof query.model === 'string' && brand) {
    let kiaSpecialIds: number[] = [];

    const kiaRanges = await getKiaRanges({});
    const value = query.model?.toString();

    const kiaRange = kiaRanges.filter((range) => {
      if (
        range.name?.toLowerCase().indexOf(value.toLowerCase()) === 0 &&
        range.name.toLowerCase() !== value.toLowerCase()
      ) {
        return true;
      }

      return (
        range.name?.replace(/ /g, '-').toLowerCase() === value.toLowerCase()
      );
    });

    kiaRange.forEach((range) => {
      kiaSpecialIds = [...kiaSpecialIds.concat(...(range?.specials || []))];
    });

    if (kiaSpecialIds && kiaSpecialIds.length > 0) {
      filter['id'] = {
        _in: kiaSpecialIds,
      };
    }
    rangeList = [...rangeList, ...kiaRange];
  }

  const carTypeFilter =
    (
      {
        'new-cars': FILTER_FOR_ALL_NEW_CAR_SPECIALS,
        'used-cars': FILTER_FOR_ALL_USED_CAR_SPECIALS,
      } as any
    )[type] || FILTER_FOR_ALL_CAR_SPECIALS;

  const { specials } = await getSpecialsByType(SPECIALS_ITEMS_COUNT_META_DATA, {
    ...carTypeFilter,
    ...filter,
  });

  const seo = {
    title: SEO_SPECIAL.DEFAULT_TITLE,
    description: SEO_SPECIAL.CATEGORY_DESCRIPTION.replace(
      /\{0\}/gi,
      type === 'used-cars' ? 'Used cars' : 'New cars',
    ),
    keyword: '',
    image: getImageLink('c3038b36-5df7-45d3-acfe-ad136266cf95'),
  };

  return {
    specials,
    otherSpecials,
    filter,
    seo,
    type,
    usedType: query.type || null,
    brand: brand?.name || null,
    model: rangeList[0]?.name || null,
  };
});

export const getSpecialsByType = limiter.wrap(async (metaQuery, filter) => {
  const offset = (metaQuery.currentPage - 1) * SPECIALS_ITEMS_COUNT || 0;
  const { data, meta }: any = await directusService.get<SpecialsReq[]>(
    'items/specials',
    {
      params: {
        filter,
        fields: ALL_SPECIALS_FIELDS,
        meta: 'filter_count',
        offset,
      },
    },
  );

  return { specials: mapSpecialsResponse(data), meta };
});

export const getSpecialByIds = limiter.wrap(
  async (filter, variableForTitleSpecials, showList) => {
    const { data, meta }: any = await directusService.get<SpecialsReq[]>(
      'items/specials',
      {
        params: {
          filter,
          fields: ALL_SPECIALS_FIELDS,
          meta: 'filter_count',
        },
      },
    );

    const title =
      data.length !== 0 || showList
        ? getSpecialTitle(variableForTitleSpecials, data)
        : '';

    const responseData = {
      id: 0,
      title: title,
      value: '',
      description: '',
      specials: mapSpecialsResponse(data),
    };

    return { responseData, meta };
  },
);

export const getOtherSpecials = limiter.wrap(async (filter = {}) => {
  const { data } = await directusService.get<SpecialsReq[]>('items/specials', {
    params: {
      limit: 3,
      filter: {
        ...filter,
        start_date: { _lte: '$NOW' },
        end_date: { _gte: '$NOW' },
        status: { _in: 'approved' },
      },
    },
  });

  return mapSpecialsResponse(data);
});

export const getSpecialById = limiter.wrap(async (id) => {
  const { data } = await directusService.get(`/items/specials/${id}`, {
    params: {
      fields: [
        '*',
        'special_category.*',
        'dealerships.dealerships_id.*',
        'body_type.*',
        'brand.id',
        'brand.name',
        'range.name',
        'range.id',
        'source',
      ],
    },
  });

  return data;
});

export const getSpecialsForVehicleByFilter = limiter.wrap(async (filter) => {
  const { data } = await directusService.get('/items/specials/', {
    params: {
      fields: [
        '*',
        'special_category.*',
        'dealerships.dealerships_id.*',
        'body_type.*',
      ],
      filter,
    },
  });

  return data;
});

export const getVehicleForSpecialsNew = limiter.wrap(
  async (filter, special = null) => {
    const { data } = await directusService.get('/items/vehicles', {
      params: {
        filter,
        fields: VEHICLES_REQUEST_FIELDS.join(','),
      },
    });
    const newData = data?.filter(
      (vehicle: any) => vehicle?.status === 'active',
    );

    const vehicles = newData?.map((el: any) =>
      mapVehicleResponse(el, undefined, special),
    );

    return vehicles;
  },
);

export const getVehicleForSpecials = limiter.wrap(
  async (filter, limit = 30, special = null) => {
    const { data } = await directusService.get('/items/vehicles', {
      params: {
        filter,
        fields: VEHICLES_REQUEST_FIELDS.join(','),
        limit,
      },
    });

    const vehicles = data?.map((el: any) =>
      mapVehicleResponse(el, undefined, special),
    );

    return vehicles;
  },
);

export const getFilterForSpecialVehicle = (special: any) => {
  const filter: any = {};
  const dealershipsIds: any[] = [];

  if (special.brand) {
    filter.brand_id = { _eq: special.brand };
  }
  if (special.range) {
    filter.range_id = { _eq: special.range };
  }

  special.dealerships.map((el: any) => {
    if (el.dealerships_id) {
      dealershipsIds.push(el.dealerships_id.id);
    }
  });

  filter.dealership_id = { _in: dealershipsIds };

  // if (special.body_type.length !== 0) {
  //   const type = [];
  //
  //   special.body_type.map((el) => {
  //     type.push(el.spec_body_types_id)
  //   })
  //   console.log(type)
  //   filter.spec_body_type = { _in: type }
  // }

  return filter;
};

export const filterForVehicleBySpecials = limiter.wrap(async (specials) => {
  const brands: any[] = [];
  const ranges: any[] = [];
  const dealerships: any[] = [];

  specials.map((el: any) => {
    brands.push(el.brand);
    ranges.push(el.range);
    el.dealerships.map((d: any) => {
      dealerships.push(d.dealerships_id);
    });
  });

  const filter = {
    brand_id: {
      _in: [...new Set(brands)],
    },
    range_id: {
      _in: [...new Set(ranges)],
    },
    dealership_id: {
      _in: [...new Set(dealerships)],
    },
  };

  return filter;
});

export const filterForVehicles = limiter.wrap(
  async (specials, dealership_id?: string) => {
    const randomSpecialsIds = [];
    const vehicles: any[] = [];

    for (let i = 0; i < 3; i++) {
      const idx = Math.floor(Math.random() * specials.length);

      randomSpecialsIds.push(specials[idx]);
      specials.splice(idx, 1);
    }

    await Promise.all(
      randomSpecialsIds.map(async (special) => {
        if (special) {
          const dealerships: string[] = [];

          const filter: {
            brand_id?: { _in: string[] };
            range_id?: { _in: string[] };
            dealership_id?: { _in: string[] };
          } = {
            brand_id: {
              _in: [special.brand?.id],
            },
            range_id: {
              _in: [special.range?.id],
            },
          };

          if (dealership_id) {
            dealerships.push(dealership_id);
          } else {
            special.dealerships.map((d: any) => {
              dealerships.push(d.dealerships_id.id);
            });

            if (dealerships.length) {
              filter.dealership_id = {
                _in: [...new Set(dealerships)],
              };
            }
          }

          const vehicle = await getVehicleForSpecials(filter, 5, special);

          if (vehicle.length !== 0) {
            return vehicles.push(...vehicle);
          }
        }
      }),
    );

    return vehicles;
  },
);

export const getSeoTitle = (data: any) => {
  const title = `${data.title} - ${SEO_SPECIAL.CAR_SPECIAL} ${KIA_GROUP}`;

  return title;
};

export const getSeoTitleTypeWise = (type: number) => {
  if (type === 3) {
    return `Kia Demo Cars ${FOR_SALE} |  ${SOUTH_AFRICA} ${KIA_GROUP}`;
  } else {
    return `Used Cars – Cars ${FOR_SALE} |  Kia ${SOUTH_AFRICA}`;
  }
};
